import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import { createHousehold } from '@/api/households'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { CURRENCY_OPTIONS, type CurrencyCode } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'

const CURRENCY_CODES = CURRENCY_OPTIONS.map((c) => c.code) as [CurrencyCode, ...CurrencyCode[]]

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Household name is required')
    .max(60, 'Household name must be 60 characters or fewer'),
  currency: z.enum(CURRENCY_CODES),
})

type FormValues = z.infer<typeof schema>

/** For creating an *additional* household once the caller already belongs to one — distinct from
 *  OnboardingPage, which only ever runs for a brand-new user with zero households. Switches into
 *  the new household immediately on success. */
export function CreateHouseholdModal({ onClose }: { onClose: () => void }) {
  const { switchHousehold } = useCurrentHousehold()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { currency: 'INR' } })

  async function onSubmit(values: FormValues) {
    try {
      const created = await createHousehold(values.name, values.currency)
      switchHousehold(created.id)
      onClose()
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      if (status === 409 && detail === 'HOUSEHOLD_LIMIT_REACHED') {
        setError('root', {
          message:
            "You're already in 3 households — the most you can be in at once. Leave or delete one, then try again.",
        })
      } else {
        setError('root', { message: 'Something went wrong. Please try again.' })
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[rgba(0,0,0,0.15)_0px_8px_40px] w-full max-w-sm mx-4 p-6">
        <h2 className="text-lg font-bold text-[var(--fl-text-1)] mb-1">Create Household</h2>
        <p className="text-sm text-[var(--fl-text-2)] mb-4">
          Sets up a separate household with its own accounts, transactions, and members.
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">
              Household name
            </label>
            <Input
              {...register('name')}
              maxLength={60}
              autoFocus
              autoComplete="off"
              placeholder="e.g. The Garcias"
            />
            {errors.name && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-2)] mb-1">
              Currency
            </label>
            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-label="Currency"
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              )}
            />
          </div>
          {errors.root && <p className="text-xs text-[var(--fl-error)]">{errors.root.message}</p>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
